import { delay, filter, map, switchMap, tap } from "rxjs/operators";
import { TActionsDefinitionsList } from "./TAction";
import { action, routeAction } from "./actionFunctions";
import { array as extArray } from "../../../../shared/src/utilByDomainGroupExport";
import { CasesPage, CasesCaseViewForm, CasesCaseViewFilterStatus, CasesCaseViewFilterChaseStatus as CasesCaseViewFilterChaseStatusCodec, CasesDayViewForm, TCasesCaseViewForm, TCasesCaseForm, CasesCaseForm, TCasesCaseCreateAdhocForm, TCasesPageView, TAdhocTaskForms, TCasesCaseTaskFormAdhocForDayView, TCasesDayViewForm, TCasesCaseViewFilterChaseStatus, CasesSailHomesReportForm, TCasesSailHomesReportForm } from "../../../../domain/codecs/form/CasesForm";
import { getPersistedCasesPageView, setPersistedCasesPageView, resolveCasesLandingView } from "./casesPageViewPersistence";
import { CasesDayViewRoadblocksForm } from "../../../../domain/codecs/form/RoadblockDayViewForm";
import { CasesDayViewBulkAwaitForm, CasesDayViewBulkRescheduleForm, CasesDayViewWeekCountsForm, TCasesDayViewBulkAwaitMode, TCasesDayViewBulkRescheduleMode } from "../../../../domain/codecs/form/CasesDayViewWeekCountsForm";
import { RoadblockAssignForm, RoadblockAwaitForm, RoadblockCancelAwaitForm, TRoadblockChecklistItemType, TRoadblockType } from "../../../../domain/codecs/form/RoadblockForm";
import { TDayWorkSource } from "../../../../domain/codecs/DayWorkSource";
import { TDaySortKey } from "../../../../domain/codecs/DaySortKey";
import { either } from "fp-ts";
import { formOperation } from "../../wrappers/formOperation";
import { Observable, from, of } from "rxjs";
import { DateTime } from "luxon";
import { TStateLens } from "../../../../domain/codecs/state";
import { TCodecSetState, TGetCodecState } from "../applyActions";
import { requireExhaustive } from "../../../../shared/src/util";
import { foldFormOnSuccess } from "../../functions/form/foldFormOnSuccess";
import { hasAssigneeLeftTheDayList, hasDateLeftTheDayList } from "../../functions/dayViewDateWindow";
import { pipe } from "fp-ts/lib/pipeable";
import { array } from "fp-ts";

type TActionParams = {
    lens: TStateLens, 
    setState: TCodecSetState, 
    getState: TGetCodecState
};

export const actions: TActionsDefinitionsList = [
    routeAction("VIEW_CRM_LEGAL_NEW_CASES", (obs$: Observable<unknown>, lens, setState, getState) => {
        obs$
            .pipe(
                tap((payload: any) => {
                    const qp = payload.queryStringParams || {};

                    const statusFromUrl = qp.status && either.isRight(CasesCaseViewFilterStatus.decode(qp.status))
                        ? qp.status
                        : undefined;
                    const chaseFromUrl = qp.chase && either.isRight(CasesCaseViewFilterChaseStatusCodec.decode(qp.chase))
                        ? qp.chase
                        : undefined;
                    const assignedToFromUrl = qp.assigned_to || undefined;
                    const introducedByFromUrl = qp.introduced_by || undefined;
                    const sailHomesFromUrl = qp.sail_homes_listing_only === "true" ? true
                        : qp.sail_homes_listing_only === "false" ? false
                        : undefined;

                    const hasCaseDeepLinkParams =
                        statusFromUrl !== undefined
                        || chaseFromUrl !== undefined
                        || assignedToFromUrl !== undefined
                        || introducedByFromUrl !== undefined
                        || sailHomesFromUrl !== undefined;
                    const landingView: TCasesPageView = resolveCasesLandingView(
                        hasCaseDeepLinkParams,
                        getPersistedCasesPageView(),
                    );

                    const dayViewSailHomesOnly = sailHomesFromUrl ?? sessionUser.is_chaser_staff;

                    const filters = {
                        ...CasesCaseViewForm.newDefault().edited.filters,
                        status: statusFromUrl
                            ?? (sessionUser.is_chaser_staff
                                ? "any_instructed"
                                : "any_instructed_excluding_abeyance_and_completed") as any,
                        assigned_to: assignedToFromUrl !== undefined
                            ? (assignedToFromUrl === "null" ? null : assignedToFromUrl)
                            : defaultCasesViewFormAssignedToUserId,
                        introduced_by: introducedByFromUrl !== undefined
                            ? (introducedByFromUrl === "null" ? null : introducedByFromUrl)
                            : null,
                        chase: chaseFromUrl ?? defaultCasesViewFormChaseMode,
                        sail_homes_listing_only: sailHomesFromUrl ?? sessionUser.is_chaser_staff,
                    };

                    setState(
                        lens.cases_page.set({
                            ...CasesPage.newDefault(),
                            view: landingView,
                            cases_view_form: {
                                ...CasesCaseViewForm.newDefault(),
                                status: landingView === "case"
                                    ? "submitting"
                                    : CasesCaseViewForm.newDefault().status,
                                edited: {
                                    ...CasesCaseViewForm.newDefault().edited,
                                    filters,
                                    order: defaultCasesViewFormOrder,
                                    pagination: {
                                        limit: 20,
                                        offset: 0,
                                    },
                                },
                            },
                            day_view_form: {
                                ...CasesDayViewForm.newDefault(),
                                status: landingView === "day"
                                    ? "submitting"
                                    : CasesDayViewForm.newDefault().status,
                                edited: {
                                    ...CasesDayViewForm.newDefault().edited,
                                    filters: {
                                        ...CasesDayViewForm.newDefault().edited.filters,
                                        assigned_to: defaultDayViewFormAssignedToUserId,
                                        date: {
                                            type: "iso",
                                            date: DateTime.utc().setZone("Europe/London").toISODate(),
                                        },
                                        sail_homes_listing_only: dayViewSailHomesOnly,
                                    },
                                    pagination: {
                                        limit: dayViewPageSize,
                                        offset: 0,
                                    },
                                },
                            },
                            day_view_roadblocks_form: {
                                ...CasesDayViewRoadblocksForm.newDefault(),
                                status: landingView === "day"
                                    ? "submitting"
                                    : CasesDayViewRoadblocksForm.newDefault().status,
                                edited: {
                                    ...CasesDayViewRoadblocksForm.newDefault().edited,
                                    filters: {
                                        ...CasesDayViewRoadblocksForm.newDefault().edited.filters,
                                        assigned_to: defaultDayViewFormAssignedToUserId,
                                        date: {
                                            type: "iso",
                                            date: DateTime.utc().setZone("Europe/London").toISODate(),
                                        },
                                        sail_homes_listing_only: dayViewSailHomesOnly,
                                    },
                                    pagination: {
                                        limit: dayViewPageSize,
                                        offset: 0,
                                    },
                                },
                            },
                            day_view_week_counts_form: {
                                ...CasesDayViewWeekCountsForm.newDefault(),
                                status: landingView === "day"
                                    ? "submitting"
                                    : CasesDayViewWeekCountsForm.newDefault().status,
                                edited: {
                                    assigned_to: defaultDayViewFormAssignedToUserId,
                                    sail_homes_listing_only: dayViewSailHomesOnly,
                                },
                            },
                            day_view_bulk_reschedule_form: CasesDayViewBulkRescheduleForm.newDefault(),
                            day_view_bulk_await_form: CasesDayViewBulkAwaitForm.newDefault(),
                            sail_homes_report_form: {
                                ...CasesSailHomesReportForm.newDefault(),
                                status: landingView === "sail_homes_report"
                                    ? "submitting"
                                    : CasesSailHomesReportForm.newDefault().status,
                                edited: {
                                    ...CasesSailHomesReportForm.newDefault().edited,
                                    filters: {
                                        ...CasesSailHomesReportForm.newDefault().edited.filters,
                                        assigned_to: null,
                                        under_offer_only: true,
                                        needs_action_only: true,
                                    },
                                    pagination: {
                                        limit: dayViewPageSize,
                                        offset: 0,
                                    },
                                },
                            },
                            cases_view_open_case_id: null,
                        }),
                    );

                    if (landingView === "case") {
                        updateCasesFilterUrl(filters);
                    }
                }),
                switchMap(() => {
                    const currentView = lens.cases_page.view.get()(getState());
                    return currentView === "case"
                        ? getCases$(lens, setState, getState)
                        : currentView === "sail_homes_report"
                        ? getSailHomesReport$(lens, setState, getState)
                        : getDayTasks$(lens, setState, getState).pipe(
                            switchMap(() => getDayRoadblocks$(lens, setState, getState)),
                            switchMap(() => getDayWeekCounts$(lens, setState, getState)),
                        );
                }),
            ).subscribe();
    }),
    action("CRM_NEW_CASES_CASE_VIEW_FILTER_CHANGE", (obs$: Observable<TCasesCaseViewForm>, lens, setState, getState) => {
        obs$.pipe(
            filter(() => lens.cases_page.cases_view_form.status.get()(getState()) !== "submitting"),
            tap(() => setCaseViewFormSubmitting(lens, setState)),
            tap((form) => {
                setState(lens.cases_page.cases_view_form.edited.filters.set(form.edited.filters));
                updateCasesFilterUrl(form.edited.filters);
            }),
            switchMap(() => resetCaseViewPagination$(lens, setState)),
            switchMap(() => clearCasesViewOpenCaseId$(lens, setState)),
            switchMap(() => getCases$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_CASE_VIEW_SORTING_CHANGE", (obs$: Observable<TCasesCaseViewForm>, lens, setState, getState) => {
        obs$.pipe(
            filter(() => lens.cases_page.cases_view_form.status.get()(getState()) !== "submitting"),
            tap(() => setCaseViewFormSubmitting(lens, setState)),
            tap((form) => setState(lens.cases_page.cases_view_form.edited.order.set(form.edited.order))),
            switchMap(() => resetCaseViewPagination$(lens, setState)),
            switchMap(() => clearCasesViewOpenCaseId$(lens, setState)),
            switchMap(() => getCases$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_CASE_VIEW_LOAD_MORE", (obs$: Observable<undefined>, lens, setState, getState) => {
        obs$.pipe(
            tap(() => setState(lens.cases_page.cases_view_form.edited.pagination.offset.set(
                lens.cases_page.cases_view_form.edited.pagination.offset.get()(getState()) + 20,
            ))),
            switchMap(() => getCases$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_CASE_VIEW_CASE_SUBMIT", (obs$: Observable<TCasesCaseForm>, lens, setState, getState) => {
        obs$.pipe(
            filter(({status}) => status !== "submitting"),
            map((form) => ({
                ...form,
                status: "submitting" as "submitting",
            })),
            tap((form) => setState(lens.cases_page.cases_view_form.children.case_forms.where(({edited}) => edited.id === form.edited.id).set(form))),
            switchMap((form) => formOperation("UpdateCasesCase", {
                ...form,
                children: CasesCaseForm.newDefault().children,
            })),
            tap((response) => setState(lens.cases_page.cases_view_form.children.case_forms.where(({edited}) => edited.id === response.edited.id).set({
                ...response,
                children: lens.cases_page.cases_view_form.children.case_forms.where(({edited}) => edited.id === response.edited.id).children.get()(getState())
            }))),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_CASE_VIEW_COMMENT_CHANGE", (obs$: Observable<TCasesCaseForm>, lens, setState) => {
        obs$.pipe(
            tap((form) => setState(lens.cases_page.cases_view_form.children.case_forms.where(({edited}) => edited.id === form.edited.id).set({
                ...form,
                status: "requiresSubmission",
            }))),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_CASE_VIEW_OPEN_CASE_ID_CHANGE", (obs$: Observable<string | null>, lens, setState) => {
        obs$.pipe(
            tap((value) => setState(lens.cases_page.cases_view_open_case_id.set(value))),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_VIEW_CHANGE", (obs$: Observable<TCasesPageView>, lens, setState, getState) => {
        obs$.pipe(
            tap((view) => setState(lens.cases_page.view.set(view))),
            tap((view) => setPersistedCasesPageView(view)),
            tap((view) => view === "day" ? clearCasesFilterUrl() : undefined),
            switchMap((view) =>
                view === "case" ? resetCaseViewPagination$(lens, setState)
                    .pipe(
                        tap(() => setCaseViewFormSubmitting(lens, setState)),
                        switchMap(() => clearCasesViewOpenCaseId$(lens, setState)),
                        switchMap(() => getCases$(lens, setState, getState)),
                    )
                : view === "day" ? resetDayViewPagination$(lens, setState)
                    .pipe(
                        tap(() => resetDayRoadblocksPagination$(lens, setState).subscribe()),
                        tap(() => setDayViewFormSubmitting(lens, setState)),
                        tap(() => setDayRoadblocksFormSubmitting(lens, setState)),
                        tap(() => setDayWeekCountsFormSubmitting(lens, setState)),
                        switchMap(() => getDayTasks$(lens, setState, getState)),
                        switchMap(() => getDayRoadblocks$(lens, setState, getState)),
                        switchMap(() => getDayWeekCounts$(lens, setState, getState)),
                    )
                : view === "sail_homes_report" ? resetSailHomesReportPagination$(lens, setState)
                    .pipe(
                        tap(() => setSailHomesReportFormSubmitting(lens, setState)),
                        switchMap(() => getSailHomesReport$(lens, setState, getState)),
                    )
                : requireExhaustive(view),
            ),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_SUBMIT_NEW_TASK", (obs$: Observable<[string, TCasesCaseCreateAdhocForm]>, lens, setState, getState) => {
        obs$.pipe(
            tap(([caseID, taskForm]) => setState(
                lens.cases_page.cases_view_form.children.case_forms
                .where((form) => form.original.id === caseID)
                .children.create_adhoc_form.status
                .set("submitting")
            )),
            switchMap(([caseID, taskForm]) => 
                from(formOperation("CreateCasesCaseAdhoc", taskForm))
                .pipe(
                    tap(foldFormOnSuccess(
                        ({ status }) => setState(
                            lens.cases_page.cases_view_form.children.case_forms
                            .where((form) => form.original.id === caseID)
                            .children.create_adhoc_form.status
                            .set(status)
                    ),
                        (response) => setState(
                            lens.cases_page.cases_view_form.children.case_forms
                            .where((form) => form.original.id === caseID)
                            .children.create_adhoc_form
                            .set(response)
                        )
                    )),

                    filter((response) => response.status === "success"),
                    delay(500),
                    switchMap(() => getCase$(caseID, { lens, setState, getState })),
                )
            ),
            
        ).subscribe()
    }),
    action("CRM_NEW_CASES_ARCHIVE_TASK", (obs$: Observable<[string, TAdhocTaskForms]>, lens, setState, getState) => {
        obs$.pipe(
            switchMap(([caseID, taskForm]) => 
                from(formOperation("DeleteCasesCaseAdhoc", taskForm))
                .pipe(
                    switchMap(() => getCase$(caseID, { lens, setState, getState })),
                )
            ),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_UPDATE_TASK", (obs$: Observable<[string, TAdhocTaskForms]>, lens, setState, getState) => {
        obs$.pipe(
            switchMap(([caseID, taskForm]) => 
                from(formOperation("UpdateCasesCaseTask", taskForm))
                .pipe(
                    switchMap(() => getCase$(caseID, { lens, setState, getState })),
                )
            ),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_DAY_VIEW_LOAD_MORE", (obs$: Observable<undefined>, lens, setState, getState) => {
        obs$.pipe(
            tap(() => setState(lens.cases_page.day_view_form.edited.pagination.offset.set(
                lens.cases_page.day_view_form.edited.pagination.offset.get()(getState()) + dayViewPageSize,
            ))),
            switchMap(() => getDayTasks$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_DAY_VIEW_ROADBLOCKS_LOAD_MORE", (obs$: Observable<undefined>, lens, setState, getState) => {
        obs$.pipe(
            tap(() => setState(lens.cases_page.day_view_roadblocks_form.edited.pagination.offset.set(
                lens.cases_page.day_view_roadblocks_form.edited.pagination.offset.get()(getState()) + dayViewPageSize,
            ))),
            switchMap(() => getDayRoadblocks$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_DAY_VIEW_ROADBLOCK_ASSIGN", (obs$: Observable<{ roadblock_id: string, assigned_to_user_id: string }>, lens, setState, getState) => {
        obs$.pipe(
            switchMap((payload) =>
                from(formOperation("AssignRoadblock", {
                    ...RoadblockAssignForm.newDefault(),
                    edited: payload,
                }))
                .pipe(
                    switchMap(() => resetDayRoadblocksPagination$(lens, setState)),
                    switchMap(() => getDayRoadblocks$(lens, setState, getState)),
                    switchMap(() => getDayWeekCounts$(lens, setState, getState)),
                )
            ),
        ).subscribe();
    }),
    action("CRM_NEW_CASES_DAY_VIEW_ROADBLOCK_FILTER_CHANGE", (obs$: Observable<{ source: TDayWorkSource, roadblock_type: TRoadblockType | null, checklist_item_type: TRoadblockChecklistItemType | null }>, lens, setState, getState) => {
        obs$.pipe(
            tap((value) => {
                setState(lens.cases_page.day_view_roadblocks_form.edited.filters.source.set(value.source));
                setState(lens.cases_page.day_view_roadblocks_form.edited.filters.roadblock_type.set(value.roadblock_type));
                setState(lens.cases_page.day_view_roadblocks_form.edited.filters.roadblock_checklist_item_type.set(value.checklist_item_type));
                setState(lens.cases_page.day_view_form.edited.filters.source.set(value.source));
                setState(lens.cases_page.day_view_form.edited.filters.roadblock_type.set(value.roadblock_type));
                setState(lens.cases_page.day_view_form.edited.filters.roadblock_checklist_item_type.set(value.checklist_item_type));
            }),
            switchMap(() => resetDayRoadblocksPagination$(lens, setState)),
            switchMap(() => resetDayViewPagination$(lens, setState)),
            tap(() => setDayRoadblocksFormSubmitting(lens, setState)),
            tap(() => setDayViewFormSubmitting(lens, setState)),
            switchMap(() => getDayRoadblocks$(lens, setState, getState)),
            switchMap(() => getDayTasks$(lens, setState, getState)),
        ).subscribe();
    }),
    action("CRM_NEW_CASES_DAY_VIEW_SORT_CHANGE", (obs$: Observable<TDaySortKey>, lens, setState, getState) => {
        obs$.pipe(
            tap((sort) => {
                setState(lens.cases_page.day_view_roadblocks_form.edited.filters.sort.set(sort));
                setState(lens.cases_page.day_view_form.edited.filters.sort.set(sort));
            }),
            switchMap(() => resetDayRoadblocksPagination$(lens, setState)),
            switchMap(() => resetDayViewPagination$(lens, setState)),
            tap(() => setDayRoadblocksFormSubmitting(lens, setState)),
            tap(() => setDayViewFormSubmitting(lens, setState)),
            switchMap(() => getDayRoadblocks$(lens, setState, getState)),
            switchMap(() => getDayTasks$(lens, setState, getState)),
        ).subscribe();
    }),
    action("CRM_NEW_CASES_DAY_VIEW_ROADBLOCK_AWAIT", (obs$: Observable<{ roadblock_id: string, awaitable_entities_id: string, days_override: number | null }>, lens, setState, getState) => {
        obs$.pipe(
            switchMap((payload) =>
                from(formOperation("AwaitRoadblock", {
                    ...RoadblockAwaitForm.newDefault(),
                    edited: payload,
                }))
                .pipe(
                    switchMap(() => resetDayRoadblocksPagination$(lens, setState)),
                    switchMap(() => getDayRoadblocks$(lens, setState, getState)),
                    switchMap(() => getDayWeekCounts$(lens, setState, getState)),
                )
            ),
        ).subscribe();
    }),
    action("CRM_NEW_CASES_DAY_VIEW_ROADBLOCK_CANCEL_AWAIT", (obs$: Observable<{ roadblock_id: string }>, lens, setState, getState) => {
        obs$.pipe(
            switchMap((payload) =>
                from(formOperation("CancelRoadblockAwait", {
                    ...RoadblockCancelAwaitForm.newDefault(),
                    edited: payload,
                }))
                .pipe(
                    switchMap(() => resetDayRoadblocksPagination$(lens, setState)),
                    switchMap(() => getDayRoadblocks$(lens, setState, getState)),
                    switchMap(() => getDayWeekCounts$(lens, setState, getState)),
                )
            ),
        ).subscribe();
    }),
    action("CRM_NEW_CASES_ARCHIVE_DAY_TASK", (obs$: Observable<TAdhocTaskForms>, lens, setState, getState) => {
        obs$.pipe(
            switchMap((taskForm) => formOperation("DeleteCasesCaseAdhoc", taskForm)),
            tap(foldFormOnSuccess(
                (response) => setState(
                    lens.cases_page.day_view_form.children.task_forms
                    .where((task) => task.original.id === response.original.id)
                    .set(response as TCasesCaseTaskFormAdhocForDayView)
                ),
                (response) => setState(
                    lens.cases_page.day_view_form.set(
                        getDayFormAfterTaskPruning(
                            lens.cases_page.day_view_form.get()(getState()),
                            [response.original.id]
                        )
                    )
                )
            )),
            tap(() => getDayWeekCounts$(lens, setState, getState).subscribe())
        ).subscribe()
    }),
    action("CRM_NEW_CASES_UPDATE_DAY_TASK", (obs$: Observable<TAdhocTaskForms>, lens, setState, getState) => {
        obs$.pipe(
            switchMap((taskForm) =>
                from(formOperation("UpdateCasesCaseTask", taskForm))
                    .pipe(
                        tap((response) => setState(
                            lens.cases_page.day_view_form.children.task_forms
                            .where((task) => task.original.id === response.original.id)
                            .set({
                                ...response,
                                children: taskForm.children,
                            } as TCasesCaseTaskFormAdhocForDayView)
                        )),
                        tap(() => getDayWeekCounts$(lens, setState, getState).subscribe()),
                        delay(300),
                        tap((response) => setState(
                            lens.cases_page.day_view_form.children.task_forms
                            .where((task) => task.original.id === response.original.id)
                            .set({
                                ...response,
                                status: "untouched",
                                children: taskForm.children,
                            } as TCasesCaseTaskFormAdhocForDayView)
                        )),
                        filter((response) => {
                            const filters = lens.cases_page.day_view_form.edited.filters.get()(getState());

                            return !!response.original.is_done
                                || hasDateLeftTheDayList(filters.date, response.original.date)
                                || hasAssigneeLeftTheDayList(filters.assigned_to, response.original.assigned_to_user_id);
                        }),
                        delay(300),
                        tap((response) => setState(
                            lens.cases_page.day_view_form.set(
                                getDayFormAfterTaskPruning(
                                    lens.cases_page.day_view_form.get()(getState()),
                                    [response.original.id]
                                )
                            )
                        ))
                    )
            ),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_DAY_VIEW_BULK_RESCHEDULE", (obs$: Observable<TCasesDayViewBulkRescheduleMode>, lens, setState, getState) => {
        obs$.pipe(
            filter(() => lens.cases_page.day_view_bulk_reschedule_form.status.get()(getState()) !== "submitting"),
            tap((mode) => setState(lens.cases_page.day_view_bulk_reschedule_form.set({
                ...CasesDayViewBulkRescheduleForm.newDefault(),
                status: "submitting",
                edited: { mode },
            }))),
            switchMap((mode) => formOperation("BulkRescheduleCasesDayTasks", {
                ...CasesDayViewBulkRescheduleForm.newDefault(),
                edited: { mode },
            })),
            tap((response) => setState(lens.cases_page.day_view_bulk_reschedule_form.set(response))),
            tap(() => setDayViewFormSubmitting(lens, setState)),
            tap(() => setDayWeekCountsFormSubmitting(lens, setState)),
            switchMap(() => resetDayViewPagination$(lens, setState)),
            switchMap(() => getDayTasks$(lens, setState, getState)),
            switchMap(() => getDayWeekCounts$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_DAY_VIEW_BULK_AWAIT", (obs$: Observable<TCasesDayViewBulkAwaitMode>, lens, setState, getState) => {
        obs$.pipe(
            filter(() => lens.cases_page.day_view_bulk_await_form.status.get()(getState()) !== "submitting"),
            tap((mode) => setState(lens.cases_page.day_view_bulk_await_form.set({
                ...CasesDayViewBulkAwaitForm.newDefault(),
                status: "submitting",
                edited: { mode },
            }))),
            switchMap((mode) => formOperation("BulkAwaitCasesDayRoadblocks", {
                ...CasesDayViewBulkAwaitForm.newDefault(),
                edited: { mode },
            })),
            tap((response) => setState(lens.cases_page.day_view_bulk_await_form.set(response))),
            tap(() => setDayViewFormSubmitting(lens, setState)),
            tap(() => setDayRoadblocksFormSubmitting(lens, setState)),
            tap(() => setDayWeekCountsFormSubmitting(lens, setState)),
            switchMap(() => resetDayViewPagination$(lens, setState)),
            switchMap(() => resetDayRoadblocksPagination$(lens, setState)),
            switchMap(() => getDayTasks$(lens, setState, getState)),
            switchMap(() => getDayRoadblocks$(lens, setState, getState)),
            switchMap(() => getDayWeekCounts$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_DAY_VIEW_BULK_AWAIT_UNDO", (obs$: Observable<undefined>, lens, setState, getState) => {
        obs$.pipe(
            map(() => lens.cases_page.day_view_bulk_await_form.get()(getState())),
            filter((form) => form.status !== "submitting" && form.children.created_block_ids.length > 0),
            switchMap((form) => formOperation("BulkAwaitCasesDayRoadblocks", {
                ...CasesDayViewBulkAwaitForm.newDefault(),
                edited: {
                    mode: {
                        type: "undo" as const,
                        block_ids: form.children.created_block_ids,
                    },
                },
            })),
            tap(() => setState(lens.cases_page.day_view_bulk_await_form.set(CasesDayViewBulkAwaitForm.newDefault()))),
            tap(() => setDayViewFormSubmitting(lens, setState)),
            tap(() => setDayRoadblocksFormSubmitting(lens, setState)),
            tap(() => setDayWeekCountsFormSubmitting(lens, setState)),
            switchMap(() => resetDayViewPagination$(lens, setState)),
            switchMap(() => resetDayRoadblocksPagination$(lens, setState)),
            switchMap(() => getDayTasks$(lens, setState, getState)),
            switchMap(() => getDayRoadblocks$(lens, setState, getState)),
            switchMap(() => getDayWeekCounts$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_DAY_VIEW_BULK_RESCHEDULE_UNDO", (obs$: Observable<undefined>, lens, setState, getState) => {
        obs$.pipe(
            map(() => lens.cases_page.day_view_bulk_reschedule_form.get()(getState())),
            filter((form) => form.status !== "submitting" && form.children.updated.length > 0),
            switchMap((form) => formOperation("BulkRescheduleCasesDayTasks", {
                ...CasesDayViewBulkRescheduleForm.newDefault(),
                edited: {
                    mode: {
                        type: "explicit" as const,
                        items: form.children.updated.map((updated) => ({
                            id: updated.id,
                            deferred_date: updated.previous_date,
                        })),
                    },
                },
            })),
            tap(() => setState(lens.cases_page.day_view_bulk_reschedule_form.set(CasesDayViewBulkRescheduleForm.newDefault()))),
            tap(() => setDayViewFormSubmitting(lens, setState)),
            tap(() => setDayWeekCountsFormSubmitting(lens, setState)),
            switchMap(() => resetDayViewPagination$(lens, setState)),
            switchMap(() => getDayTasks$(lens, setState, getState)),
            switchMap(() => getDayWeekCounts$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_DAY_VIEW_FILTER_CHANGE", (obs$: Observable<TCasesDayViewForm>, lens, setState, getState) => {
        obs$.pipe(
            filter(() => lens.cases_page.day_view_form.status.get()(getState()) !== "submitting"),
            tap(() => setDayViewFormSubmitting(lens, setState)),
            tap(() => setDayRoadblocksFormSubmitting(lens, setState)),
            tap((form) => {
                const roadblocksFilters = lens.cases_page.day_view_roadblocks_form.edited.filters.get()(getState());
                const sharedFilters = {
                    ...form.edited.filters,
                    source: roadblocksFilters.source,
                    roadblock_type: roadblocksFilters.roadblock_type,
                    roadblock_checklist_item_type: roadblocksFilters.roadblock_checklist_item_type,
                };
                setState(lens.cases_page.day_view_form.edited.filters.set(sharedFilters));
                setState(lens.cases_page.day_view_roadblocks_form.edited.filters.set(sharedFilters));
            }),
            tap((form) => setState(lens.cases_page.day_view_week_counts_form.edited.assigned_to.set(form.edited.filters.assigned_to))),
            tap((form) => setState(lens.cases_page.day_view_week_counts_form.edited.sail_homes_listing_only.set(form.edited.filters.sail_homes_listing_only))),
            tap(() => setDayWeekCountsFormSubmitting(lens, setState)),
            switchMap(() => resetDayViewPagination$(lens, setState)),
            switchMap(() => resetDayRoadblocksPagination$(lens, setState)),
            switchMap(() => getDayTasks$(lens, setState, getState)),
            switchMap(() => getDayRoadblocks$(lens, setState, getState)),
            switchMap(() => getDayWeekCounts$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_SAIL_HOMES_REPORT_FILTER_CHANGE", (obs$: Observable<TCasesSailHomesReportForm>, lens, setState, getState) => {
        obs$.pipe(
            filter(() => lens.cases_page.sail_homes_report_form.status.get()(getState()) !== "submitting"),
            tap((form) => setState(lens.cases_page.sail_homes_report_form.edited.filters.set(form.edited.filters))),
            tap(() => setSailHomesReportFormSubmitting(lens, setState)),
            switchMap(() => resetSailHomesReportPagination$(lens, setState)),
            switchMap(() => getSailHomesReport$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_SAIL_HOMES_REPORT_LOAD_MORE", (obs$: Observable<undefined>, lens, setState, getState) => {
        obs$.pipe(
            tap(() => setState(lens.cases_page.sail_homes_report_form.edited.pagination.offset.set(
                lens.cases_page.sail_homes_report_form.edited.pagination.offset.get()(getState()) + dayViewPageSize,
            ))),
            switchMap(() => getSailHomesReport$(lens, setState, getState)),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_SAIL_HOMES_REPORT_EXPAND", (obs$: Observable<string>, lens, setState, getState) => {
        obs$.pipe(
            filter((caseId) => !lens.cases_page.sail_homes_report_form.children.expanded_case_forms.get()(getState()).some((f) => f.original.id === caseId)),
            switchMap((caseId) =>
                from(formOperation("GetCasesCaseViewCase", getCaseForm(caseId)))
                    .pipe(
                        tap((response) => setState(lens.cases_page.sail_homes_report_form.children.expanded_case_forms.set([
                            ...lens.cases_page.sail_homes_report_form.children.expanded_case_forms.get()(getState()),
                            response,
                        ]))),
                    )
            ),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_SAIL_HOMES_REPORT_SUBMIT_NEW_TASK", (obs$: Observable<[string, TCasesCaseCreateAdhocForm]>, lens, setState, getState) => {
        obs$.pipe(
            tap(([caseID]) => setState(
                lens.cases_page.sail_homes_report_form.children.expanded_case_forms
                .where((form) => form.original.id === caseID)
                .children.create_adhoc_form.status
                .set("submitting")
            )),
            switchMap(([caseID, taskForm]) =>
                from(formOperation("CreateCasesCaseAdhoc", taskForm))
                .pipe(
                    tap(foldFormOnSuccess(
                        ({ status }) => setState(
                            lens.cases_page.sail_homes_report_form.children.expanded_case_forms
                            .where((form) => form.original.id === caseID)
                            .children.create_adhoc_form.status
                            .set(status)
                        ),
                        (response) => setState(
                            lens.cases_page.sail_homes_report_form.children.expanded_case_forms
                            .where((form) => form.original.id === caseID)
                            .children.create_adhoc_form
                            .set(response)
                        )
                    )),
                    filter((response) => response.status === "success"),
                    delay(500),
                    switchMap(() => getSailHomesReportCase$(caseID, { lens, setState, getState })),
                )
            ),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_SAIL_HOMES_REPORT_ARCHIVE_TASK", (obs$: Observable<[string, TAdhocTaskForms]>, lens, setState, getState) => {
        obs$.pipe(
            switchMap(([caseID, taskForm]) =>
                from(formOperation("DeleteCasesCaseAdhoc", taskForm))
                .pipe(
                    switchMap(() => getSailHomesReportCase$(caseID, { lens, setState, getState })),
                )
            ),
        ).subscribe()
    }),
    action("CRM_NEW_CASES_SAIL_HOMES_REPORT_UPDATE_TASK", (obs$: Observable<[string, TAdhocTaskForms]>, lens, setState, getState) => {
        obs$.pipe(
            switchMap(([caseID, taskForm]) =>
                from(formOperation("UpdateCasesCaseTask", taskForm))
                .pipe(
                    switchMap(() => getSailHomesReportCase$(caseID, { lens, setState, getState })),
                )
            ),
        ).subscribe()
    }),
];

const getCase$ = (caseID: string, { lens, setState }: TActionParams ) => 
    from(formOperation("GetCasesCaseViewCase", getCaseForm(caseID)))
        .pipe(
            tap((response) => setState(
                lens.cases_page.cases_view_form.children.case_forms
                .where((form) => form.original.id === response.original.id)
                .set(response)                                                                                                                                                                                                                                                                      
            ))
        )
;

const getCaseForm = (id: string) => {
    const defaultForm = CasesCaseForm.newDefault();
    return {
        ...defaultForm,
        edited: { ...defaultForm.edited, id }
    }
}

const getCases$ = (lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) =>
    from(
        formOperation("GetCasesCaseView", {
            ...lens.cases_page.cases_view_form.get()(getState()),
            children: CasesCaseViewForm.newDefault().children,
        })
    )
        .pipe(
            delay(100),
            tap((response) =>
                lens.cases_page.cases_view_form.edited.pagination.offset.get()(getState()) === 0
                    ? setState(lens.cases_page.cases_view_form.set(response))
                    : setState(lens.cases_page.cases_view_form.set({
                        ...response,
                        children: {
                            ...response.children,
                            case_forms: [
                                ...lens.cases_page.cases_view_form.children.case_forms.get()(getState()),
                                ...response.children.case_forms,
                            ]
                        }
                    })),
            ),
        );

const resetCaseViewPagination$ = (lens: TStateLens, setState: TCodecSetState) =>
    of(
        setState(lens.cases_page.cases_view_form.edited.pagination.set({
            offset: 0,
            limit: 20
        })),
    );

const clearCasesViewOpenCaseId$ = (lens: TStateLens, setState: TCodecSetState) =>
    of(
        setState(lens.cases_page.cases_view_open_case_id.set(null)),
    );

const resetDayViewPagination$ = (lens: TStateLens, setState: TCodecSetState) =>
    of(
        setState(lens.cases_page.day_view_form.edited.pagination.set({
            offset: 0,
            limit: dayViewPageSize
        })),
    );

const getDayTasks$ = (lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) =>
    from(
        formOperation("GetCasesDayView", {
            ...lens.cases_page.day_view_form.get()(getState()),
            children: CasesDayViewForm.newDefault().children,
        })
    )
        .pipe(
            delay(100),
            tap((response) =>
                lens.cases_page.day_view_form.edited.pagination.offset.get()(getState()) === 0
                    ? setState(lens.cases_page.day_view_form.set(response))
                    : setState(lens.cases_page.day_view_form.set({
                        ...response,
                        children: {
                            ...response.children,
                            task_forms: [
                                ...lens.cases_page.day_view_form.children.task_forms.get()(getState()),
                                ...response.children.task_forms,
                            ]
                        }
                    })),
            ),
        );

const setCaseViewFormSubmitting = (lens: TStateLens, setState: TCodecSetState) => setState(lens.cases_page.cases_view_form.status.set("submitting"));

const setDayViewFormSubmitting = (lens: TStateLens, setState: TCodecSetState) => setState(lens.cases_page.day_view_form.status.set("submitting"));

const setDayRoadblocksFormSubmitting = (lens: TStateLens, setState: TCodecSetState) =>
    setState(lens.cases_page.day_view_roadblocks_form.status.set("submitting"));

const resetDayRoadblocksPagination$ = (lens: TStateLens, setState: TCodecSetState) =>
    of(
        setState(lens.cases_page.day_view_roadblocks_form.edited.pagination.set({
            offset: 0,
            limit: dayViewPageSize,
        })),
    );

const setDayWeekCountsFormSubmitting = (lens: TStateLens, setState: TCodecSetState) =>
    setState(lens.cases_page.day_view_week_counts_form.status.set("submitting"));

const getDayWeekCounts$ = (lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) =>
    from(
        formOperation("GetCasesDayViewWeekCounts", {
            ...lens.cases_page.day_view_week_counts_form.get()(getState()),
            children: CasesDayViewWeekCountsForm.newDefault().children,
        }),
    )
        .pipe(
            tap((response) => setState(lens.cases_page.day_view_week_counts_form.set(response))),
        );

const getDayRoadblocks$ = (lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) =>
    from(
        formOperation("GetCasesDayViewRoadblocks", {
            ...lens.cases_page.day_view_roadblocks_form.get()(getState()),
            children: CasesDayViewRoadblocksForm.newDefault().children,
        }),
    )
        .pipe(
            delay(100),
            tap((response) =>
                lens.cases_page.day_view_roadblocks_form.edited.pagination.offset.get()(getState()) === 0
                    ? setState(lens.cases_page.day_view_roadblocks_form.set(response))
                    : setState(lens.cases_page.day_view_roadblocks_form.set({
                        ...response,
                        children: {
                            ...response.children,
                            roadblocks: [
                                ...lens.cases_page.day_view_roadblocks_form.children.roadblocks.get()(getState()),
                                ...response.children.roadblocks,
                            ],
                        },
                    })),
            ),
        );

const setSailHomesReportFormSubmitting = (lens: TStateLens, setState: TCodecSetState) =>
    setState(lens.cases_page.sail_homes_report_form.status.set("submitting"));

const resetSailHomesReportPagination$ = (lens: TStateLens, setState: TCodecSetState) =>
    of(
        setState(lens.cases_page.sail_homes_report_form.edited.pagination.set({
            offset: 0,
            limit: dayViewPageSize,
        })),
    );

const getSailHomesReport$ = (lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) =>
    from(
        formOperation("GetCasesSailHomesReport", {
            ...lens.cases_page.sail_homes_report_form.get()(getState()),
            children: CasesSailHomesReportForm.newDefault().children,
        }),
    )
        .pipe(
            delay(100),
            tap((response) =>
                lens.cases_page.sail_homes_report_form.edited.pagination.offset.get()(getState()) === 0
                    ? setState(lens.cases_page.sail_homes_report_form.set(response))
                    : setState(lens.cases_page.sail_homes_report_form.set({
                        ...response,
                        children: {
                            ...response.children,
                            report_rows: [
                                ...lens.cases_page.sail_homes_report_form.children.report_rows.get()(getState()),
                                ...response.children.report_rows,
                            ],
                            expanded_case_forms: lens.cases_page.sail_homes_report_form.children.expanded_case_forms.get()(getState()),
                        },
                    })),
            ),
        );

const getSailHomesReportCase$ = (caseID: string, { lens, setState, getState }: TActionParams) =>
    from(formOperation("GetCasesCaseViewCase", getCaseForm(caseID)))
        .pipe(
            tap((response) => setState(
                lens.cases_page.sail_homes_report_form.children.expanded_case_forms
                .where((form) => form.original.id === response.original.id)
                .set(response)
            )),
        );

const getDayFormAfterTaskPruning = (form: TCasesDayViewForm, taskIDsToPrune: Array<string>): TCasesDayViewForm => {
    const prunedTasks = pipe(
        form.children.task_forms,
        array.filter((email) => !extArray.contains(email.original.id)(taskIDsToPrune)),
    );
    const numberPruned = form.children.task_forms.length - prunedTasks.length;

    return {
        ...form,
        children: {
            ...form.children,
            task_forms: prunedTasks,
            available_count: form.children.available_count - numberPruned
        }
    };
}

const defaultCasesViewFormAssignedToUserId =
    sessionUser.user_role === "sail_legal_authoriser_staff" ? sessionUser.id
    : null;

const defaultDayViewFormAssignedToUserId =
    sessionUser.user_role !== "user" ? sessionUser.id
    : null;

const dayViewPageSize = 30;

const defaultCasesViewFormChaseMode: TCasesCaseViewFilterChaseStatus =
    sessionUser.user_role === "sail_legal_authoriser_staff" ? "workable"
    : sessionUser.is_chaser_staff ? "chase"
    : "any";

const defaultCasesViewFormOrder =
    sessionUser.is_chaser_staff
        ? {
            column: "chase_mode",
            direction: "ascending",
        } as const
        : null;

const updateCasesFilterUrl = (filters: TCasesCaseViewForm["edited"]["filters"]) => {
    const params: Record<string, string> = {
        status: filters.status,
        chase: filters.chase,
        sail_homes_listing_only: String(filters.sail_homes_listing_only),
    };
    if (filters.assigned_to) {
        params.assigned_to = filters.assigned_to;
    }
    if (filters.introduced_by) {
        params.introduced_by = filters.introduced_by;
    }
    const queryString = Object.entries(params)
        .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
        .join("&");
    window.history.replaceState(undefined, "Sail Homes", `/crm/legal/cases?${queryString}`);
};

const clearCasesFilterUrl = () =>
    window.history.replaceState(undefined, "Sail Homes", "/crm/legal/cases");
